import { get, postJson, postFormData, postForm } from "@/utils/request";
import { WithPagingData, PagingParameters } from "@/api/util";
import env from "@/env";

export interface IReqDemandPoint {
  jobType: string;
  machineType: string;
  locId: number | string;
}
export interface IResDemandPoint {
  jobTypeName: string;
  machineTypeName: string;
  landArea: string;
  createTime: string;
  locInfo: string;
  address: string;
  lat: number;
  lng: number;
  id: number;
}
/**
 * @description: 获取需求列表
 * @returns { IResDemandPoint[] }
 */
export const getMapDemandPointData = (
  data: IReqDemandPoint | PagingParameters
) => {
  return get<WithPagingData<IResDemandPoint[]>>({
    baseURL: env.nongshiUrl,
    url: "/social/machineRequirement/findPage",
    data,
  });
};

export interface IResDemandDetails {
  title: string;
  jobTypeName: string;
  machineTypeName: string;
  landArea: string;
  jobTimeStart: string;
  jobTimeEnd: string;
  locInfo: string;
  address: string;
  machineCanEnter: number;
  price: string;
  priceUnitName: string;
  lat: number;
  lng: number;
}
/**
 * @description: 获取需求详情
 * @param { Number } id
 * @returns { IResDemandDetails }
 */
export const getDemandDetails = (id: number) => {
  return get<IResDemandDetails>({
    baseURL: env.nongshiUrl,
    url: "/social/machineRequirement/findDetail",
    data: { id },
  });
};

export interface IReqServicePoint {
  jobType: string;
  machineType: string;
  locId: number | string;
}
export interface IResServicePoint {
  lat: number;
  lng: number;
  id: number;
}
/**
 * @description: 获取服务列表
 * @returns { IResDemandPoint[] }
 */
export const getMapServicePointData = (
  data: IReqServicePoint | PagingParameters
) => {
  return get<WithPagingData<IResServicePoint[]>>({
    baseURL: env.nongshiUrl,
    url: "/social/serviceHall/findPage",
    data,
  });
};

export interface IReqServiceDetails {
  id: number;
  sf: string;
}
export interface IResServiceDetails {
  id: number;
  sf: string;
  fullName?: string;
  personName?: string;
  phone?: string;
  locInfo?: string;
  address?: string;
  serviceMachineTypes?: string[];
  lat?: number;
  lng?: number;
}
/**
 * @description: 获取服务详情
 * @param { Number } id
 * @returns { IResServiceDetails }
 */
export const getServiceDetails = (data: IReqServiceDetails) => {
  return get<IResServiceDetails>({
    baseURL: env.nongshiUrl,
    url: "/social/serviceHall/details",
    data,
  });
};

export interface IResJobType {
  id: number;
  name: string;
}
/**
 * @description: 获取作业类型
 * @param { Number } pid
 * @return { IResJobType[] }
 */
export const getJobType = (pid: number = 0) => {
  return get<IResJobType[]>({
    baseURL: env.nongshiUrl,
    url: "/common/jobType/queryJobTypesByPid",
    data: { pid },
  });
};

export interface IResMachineryType {
  id: number;
  machineTypeName: string;
}
/**
 * @description: 获取农机类型
 * @param { Number } jobTypeId
 * @return { IResMachineryType[] }
 */
export const getMachineryType = (jobTypeId: number) => {
  return get<IResMachineryType[]>({
    baseURL: env.nongshiUrl,
    url: "/social/machineType/getListByJob",
    data: { jobTypeId },
  });
};

export interface IReqSupplyMachinery {
  id: number;
  sf: string;
}
/**
 * @description: 获取供应农机
 * @param { Number } id
 * @returns { any }
 */
export const getSupplyMachinery = (data: IReqSupplyMachinery) => {
  return get<any>({
    baseURL: env.nongshiUrl,
    url: "/social/machine/findServiceMachineTypeList",
    data,
  });
};

export interface IReqMachinery {
  dataChannel: string;
  lng: string;
  lat: string;
  jobType: string;
  machineType: string | number;
  landArea: string;
  jobDate: string;
  jobTimeEnd: string;
  jobTimeStart: string;
  locId: string;
  fillAddress: string;
  address: string;
  machineCanEnter: string;
  price: string;
  priceUnit: string;
  title: string;
  contactName: string;
  contactPhone: string;
}
/**
 * @description: 新增农机
 * @param { IReqMachinery } data
 * @returns { Void }
 */
export const setMachinery = (data: IReqMachinery) => {
  return postJson({
    baseURL: env.nongshiUrl,
    url: "/social/machineRequirement/save",
    data,
  });
};

export interface IResDict {
  code: number;
  value: string;
}
/**
 * @description: 获取字典
 * @param { String } type
 * @returns { Void }
 */
export const getDict = (type: String) => {
  return get<IResDict[]>({
    baseURL: env.nongshiUrl,
    url: "/common/dict/querySysDictlistByType",
    data: { type },
  });
};

export interface IReqLngLatToAddress {
  lng: number;
  lat: number;
}
/**
 * 获取经纬度转地区信息
 * @param { IReqLngLatToAddress } data
 * @returns
 */
export const getLngLatToAddress = (data: IReqLngLatToAddress) => {
  return get<{
    address: string;
    locInfo: {
      province: { id: string; name: string };
      city: { id: string; name: string };
      county: { id: string; name: string };
      town: { id: string; name: string };
    };
  }>({
    baseURL: env.centerUrl,
    url: "/soil/locInfo/coordinateTransferToLocInfo",
    data,
  });
};

/**
 * @description: 获取 app 二维码
 * @param { String } type
 * @returns { Void }
 */
export const getQRCode = (type: String) => {
  return get<{ nongshi: string; social: string }>({
    baseURL: env.nongshiUrl,
    url: "/common/dict/querySysDictMapByType",
    data: { type },
  });
};

export interface IReqCooperative {
  dataChannel: string;
  appId: string;
  state: number;
  lng: string;
  lat: string;
  cooperativeName: string;
  locId: string;
  fillAddress: string;
  address: string;
  personName: string;
  phone: string;
  busiLicense: string;
  doorHeads: string;
}
/**
 * @description: 合作社入驻
 * @param { IReqCooperative } data
 * @returns { Void }
 */
export const setCooperative = (data: IReqCooperative) => {
  return postJson({
    baseURL: env.nongshiUrl,
    url: "/user/apply/cooperativeApply",
    data,
  });
};

export interface IReqMachineryOwner {
  dataChannel: string;
  appId: string;
  state: number;
  lng: string;
  lat: string;
  farmName: string;
  idNumber: string;
  locId: string;
  fillAddress: string;
  address: string;
  drivingLicense: string;
  phone: string;
}
/**
 * @description: 农机主入驻
 * @param { IReqMachineryOwner } data
 * @returns { Void }
 */
export const setMachineryOwner = (data: IReqMachineryOwner) => {
  return postJson({
    baseURL: env.nongshiUrl,
    url: "/user/apply/farmMachineryApply",
    data,
  });
};

/**
 * @description: 上传文件
 * @param { File } file
 * @returns { Object }
 */
export function uploadFile(data: { file: File }) {
  return postFormData<{ signatureUrl: string; fileUrl: string }>({
    url: "/common/oss/uploadImg",
    data,
  });
}

/**
 * 获取协议
 * @returns { Object }
 */
export const getConfigAgreement = () => {
  return get<{ [key: string]: { content: string } }>({
    baseURL: env.centerUrl,
    url: "/ddd/basic/queryConfigAgreement",
  });
};

export interface IReqPhoneCode {
  client_id: string;
  auto_regist: boolean;
  phone: string;
}
/**
 * 获取手机验证码
 * @param { IReqPhoneCode } data
 * @returns { Void }
 */
export const getPhoneCode = (data: IReqPhoneCode) => {
  return postForm({
    baseURL: env.centerUrl,
    url: "/UAA/oauth/sendSmsCode",
    data,
  });
};

export interface IReqSignIn {
  client_id: string;
  client_secret: string;
  grant_type: string;
  auto_regist: boolean;
  phone: string;
  code: string;
  agreement?: string | number | boolean;
}
export interface IResSignIn {
  access_token: string;
}
/**
 * 登录
 * @param { IReqSignIn } data
 * @returns { Void }
 */
export const setSignIn = (data: IReqSignIn) => {
  return postForm<IResSignIn>({
    baseURL: env.centerUrl,
    url: "/UAA/oauth/token",
    data,
  });
};

export interface IResSignIn {
  phone: string;
}
/**
 * 获取用户信息
 * @returns { IResSignIn }
 */
export const getSignInInfo = () => {
  return get<IResSignIn>({
    baseURL: env.centerUrl,
    url: "/user/user/getBaseInfo",
  });
};
